/**
 * PittRigidPage.tsx  –  PittRigid ME
 *
 * Layout rules
 * ─────────────
 * • max-w-7xl container, px-6 gutter  →  ~75–80% of typical 1440px screen
 * • Single white form card, internal <hr> section breaks
 * • Every input row uses a strict 4-column grid (grid-cols-4)
 *   – narrow fields (numbers, short selects) = 1 col
 *   – wide fields (climate, traffic pattern)  = 2 cols
 *   – on mobile: grid-cols-1; on sm: grid-cols-2
 * • Labels: text-sm (14px), font-medium, gray-700 — readable at all ages
 * • Settings panel (hidden by default) contains:
 *   reliability %, design targets, calibration coefficients
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  Title, Tooltip, Legend, Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import {
  usePittRigid,
  DOWEL_DIAMETERS,
  LANE_FACTORS,
  PRESETS,
  type PittRigidInputs,
  type PittRigidResults,
  type PresetKey,
  type ModelCoeffs,
} from '../hooks/usePittRigid';
import {
  computeIriSeries,
  type IriSeries,
} from '../hooks/iriModel';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const monoFont  = { fontFamily: "'DM Mono', monospace" };
const serifFont = { fontFamily: "'Crimson Pro', Georgia, serif" };

const inputCls  = 'w-full rounded border border-gray-300 px-3 py-2 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-[#003594]/30 focus:border-[#003594] transition-colors';
const selectCls = [
  'w-full rounded border border-gray-300 pl-3 pr-8 py-2 text-sm text-gray-800 bg-white',
  'focus:outline-none focus:ring-2 focus:ring-[#003594]/30 focus:border-[#003594] transition-colors',
  // SVG arrow positioned inside the pr-8 zone, flush with border
  "bg-[url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='none' stroke='%236b7280' stroke-width='1.5' d='M2 4l4 4 4-4'/%3E%3C/svg%3E\")] bg-no-repeat bg-[right_0.6rem_center]",
].join(' ');

const PITT_BLUE = '#003594';
const PITT_GOLD = '#FFB81C';
const RED_LINE  = '#ef4444';

// MEPDG terminal IRI design limit (PennDOT uses 175 in/mi). Default only —
// the user can override it via the Target IRI input. Shown as a reference
// line on the IRI chart; it does NOT drive the thickness/dowel design.
const DEFAULT_TARGET_IRI = 175;

const CLIMATE_OPTIONS = [
  'Region 1: Erie County',
  'Region 2: PennDOT Districts D1 (except Erie Co.), D10, D11, D12',
  'Region 3: PennDOT Districts D2 and D9',
  'Region 4: PennDOT Districts D3 and D4',
  'Region 5: PennDOT Districts D5, D6, and D8',
];
const TRAFFIC_PATTERNS = [
  'Urban Principal Arterial - Interstate',
  'Rural Principal Arterial-Interstate',
  'Minor Arterial, Collectors, and Recreational',
];
const BASE_TYPES     = ['Aggregate', 'Permeable asphalt-treated', 'Permeable cement-treated'];
const SHOULDER_TYPES = ['Tied PCC', 'Asphalt/Non-Tied PCC/Aggregate'];
const SLAB_WIDTHS    = ['Conventional width (12 ft)', 'Widened lane'];
const JOINT_SPACINGS = [12, 15];
const COTE_VALUES    = [4.5, 5.0, 5.5];
const LANES_OPTIONS  = [2, 4, 6, 8];
const PRESET_LABELS: Record<PresetKey, string> = {
  national:       'National defaults',
  penndot_new:    'PennDOT defaults with new faulting model',
  penndot_paveme: 'PennDOT defaults with Pavement ME faulting model',
  custom:         'Custom',
};

// ─── NumInput ─────────────────────────────────────────────────────────────────
const NumInput: React.FC<{
  value: number; onChange: (n: number) => void;
  className?: string; step?: string; min?: string; max?: string;
}> = ({ value, onChange, className, step, min, max }) => {
  const [draft, setDraft] = useState(String(value));
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) setDraft(String(value)); }, [value]);
  const commit = () => {
    focused.current = false;
    const n = parseFloat(draft);
    if (!isNaN(n)) { onChange(n); setDraft(String(n)); } else setDraft(String(value));
  };
  return (
    <input type="text" inputMode="decimal" step={step} min={min} max={max}
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onFocus={() => { focused.current = true; }}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      className={className ?? inputCls}
    />
  );
};

// ─── Field — label + control, sized by parent grid ───────────────────────────
const Field: React.FC<{ label: React.ReactNode; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    {children}
  </div>
);

// ─── Section heading with rule ────────────────────────────────────────────────
const SectionHead: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex items-center gap-3 mb-4">
    <h3 style={serifFont} className="text-base font-semibold text-[#001f5b] whitespace-nowrap">{children}</h3>
    <div className="flex-1 border-t border-gray-200" />
  </div>
);

// ─── CoeffRow ─────────────────────────────────────────────────────────────────
const CoeffRow: React.FC<{ label: string; value: number; onChange: (n: number) => void; readOnly?: boolean }> = ({ label, value, onChange, readOnly }) => (
  <div className="flex items-center gap-2">
    <span style={monoFont} className="text-xs font-medium text-[#003594] w-7 flex-shrink-0">{label}</span>
    <input
      type="text"
      readOnly={readOnly}
      value={value}
      onChange={readOnly ? undefined : e => { const n = parseFloat(e.target.value); if (!isNaN(n)) onChange(n); }}
      className={readOnly
        ? 'w-full rounded border border-gray-200 px-3 py-2 text-sm text-gray-500 bg-gray-50 cursor-default select-none'
        : 'w-full rounded border border-gray-300 px-3 py-2 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-[#003594]/30 focus:border-[#003594] transition-colors'}
    />
  </div>
);

// ─── Climate map modal ────────────────────────────────────────────────────────
const ClimateMapModal: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
    onClick={onClose}>
    <div className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full overflow-hidden"
      onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
        <span style={serifFont} className="text-lg font-semibold text-[#001f5b]">Pennsylvania Climate Regions</span>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-700 transition-colors p-1 rounded">
          <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
      <img src="/climate.png" alt="Pennsylvania PennDOT climate regions" className="w-full h-auto" />
    </div>
  </div>
);

// ─── Charts ───────────────────────────────────────────────────────────────────
const chartOptions = (yLabel: string, yMax?: number) => ({
  responsive: true, maintainAspectRatio: false,
  interaction: { mode: 'index' as const, intersect: false },
  plugins: {
    legend: { position: 'top' as const, labels: { font: { size: 12 }, boxWidth: 12, padding: 12 } },
    tooltip: { bodyFont: { size: 12 }, titleFont: { size: 12 } },
  },
  scales: {
    x: {
      title: { display: true, text: 'Pavement age (years)', font: { size: 12 } },
      grid: { color: 'rgba(0,0,0,0.05)' },
      ticks: { maxRotation: 0, minRotation: 0, autoSkip: false, callback: (_: unknown, i: number, ticks: unknown[]) => { const v = i + 1; const last = ticks.length; const step = last <= 20 ? 5 : 10; return (v === 1 || v % step === 0 || i === last - 1) ? v : undefined; } },
    },
    y: { title: { display: true, text: yLabel, font: { size: 12 } }, max: yMax, min: 0, grid: { color: 'rgba(0,0,0,0.05)' } },
  },
});

const CrackingChart: React.FC<{ results: PittRigidResults; relCrack: number; targetSlabs: number }> = ({ results, relCrack, targetSlabs }) => {
  const labels = Array.from({ length: results.crackAge.length }, (_, i) => i + 1);
  // Scale the y-axis so the target line sits near the top, just like the faulting chart.
  // Take the max of: the target, the highest reliability-adjusted value, and the 50% curve,
  // then add 20% headroom and round up to a clean number.
  const dataMax = Math.max(targetSlabs, ...results.crackAgeRel, ...results.crackAge);
  const yMax = Math.ceil(dataMax * 1.2);
  return (
    <div style={{ height: 280 }}>
      <Line data={{ labels, datasets: [
        { label: '50% reliability', data: results.crackAge, borderColor: PITT_BLUE, backgroundColor: `${PITT_BLUE}18`, borderWidth: 2, pointRadius: 1.5, fill: false, tension: 0.3 },
        { label: `${relCrack}% reliability`, data: results.crackAgeRel, borderColor: PITT_GOLD, backgroundColor: `${PITT_GOLD}18`, borderWidth: 2, pointRadius: 1.5, fill: false, tension: 0.3 },
        { label: 'Target', data: labels.map(() => targetSlabs), borderColor: RED_LINE, borderWidth: 1.5, borderDash: [5, 4], pointRadius: 0, fill: false, tension: 0 },
      ]}} options={chartOptions('Cracked slabs (%)', yMax)} />
    </div>
  );
};

const FaultingChart: React.FC<{ results: PittRigidResults; relFault: number; targetFault: number }> = ({ results, relFault, targetFault }) => {
  const labels = Array.from({ length: results.fault.length }, (_, i) => i + 1);
  return (
    <div style={{ height: 280 }}>
      <Line data={{ labels, datasets: [
        { label: '50% reliability', data: results.fault, borderColor: PITT_BLUE, backgroundColor: `${PITT_BLUE}18`, borderWidth: 2, pointRadius: 1.5, fill: false, tension: 0.3 },
        { label: `${relFault}% reliability`, data: results.faultRel, borderColor: PITT_GOLD, backgroundColor: `${PITT_GOLD}18`, borderWidth: 2, pointRadius: 1.5, fill: false, tension: 0.3 },
        { label: 'Target', data: labels.map(() => targetFault), borderColor: RED_LINE, borderWidth: 1.5, borderDash: [5, 4], pointRadius: 0, fill: false, tension: 0 },
      ]}} options={chartOptions('Joint faulting (in)')} />
    </div>
  );
};

// ─── IRI charts ─────────────────────────────────────────────────────────────
// IRI development: 50% (predicted mean) and target-reliability curves.
const IRIChart: React.FC<{ series: IriSeries; relIri: number; targetIri: number | null }> = ({ series, relIri, targetIri }) => {
  const labels = series.years.map(y => y.age);
  const iri50  = series.years.map(y => y.iri50);
  const iriRel = series.years.map(y => y.iriRel);
  const dataMax = Math.max(...iriRel, ...(targetIri ? [targetIri] : []));
  const yMax = Math.ceil((dataMax * 1.1) / 10) * 10;

  const datasets: Record<string, unknown>[] = [
    { label: '50% reliability', data: iri50, borderColor: PITT_BLUE, backgroundColor: `${PITT_BLUE}18`, borderWidth: 2, pointRadius: 1.5, fill: false, tension: 0.3 },
    { label: `${relIri}% reliability`, data: iriRel, borderColor: PITT_GOLD, backgroundColor: `${PITT_GOLD}18`, borderWidth: 2, pointRadius: 1.5, fill: false, tension: 0.3 },
  ];
  if (targetIri != null) {
    datasets.push({ label: 'Design limit', data: labels.map(() => targetIri), borderColor: RED_LINE, borderWidth: 1.5, borderDash: [5, 4], pointRadius: 0, fill: false, tension: 0 });
  }

  return (
    <div style={{ height: 280 }}>
      <Line data={{ labels, datasets }} options={chartOptions('IRI (in/mi)', yMax)} />
    </div>
  );
};

// ─── Traffic series helper (mirrors Java mainJFrame lines 1907–1912) ─────────
// track2Esal factors per traffic pattern:
//   iTraf=0 (Urban Interstate):  21.58 / 2000 / 20 / 365
//   iTraf=1 (Urban Arterial):    23.36 / 2000 / 20 / 365
//   iTraf=2 (Rural):             12.93 / 2000 / 20 / 365
// truckCum[i] = truckCum[i-1] + 365 * traf0 * (1+rate/100)^(i-1) * 0.5
// esalCum[i]  = truckCum[i] * track2Esal / 0.95   (result is in millions)
const TRACK2ESAL = [
  21.58 / 2000 / 20 / 365,   // Urban Interstate
  23.36 / 2000 / 20 / 365,   // Urban Arterial
  12.93 / 2000 / 20 / 365,   // Rural
];

function computeTrafficSeries(traf0: number, rate: number, iLife: number, iTrafficPattern: number) {
  const t2e = TRACK2ESAL[iTrafficPattern] ?? TRACK2ESAL[2];
  const truckCum: number[] = [0];
  const esalCum:  number[] = [0];
  for (let i = 1; i <= iLife; i++) {
    const trucks = truckCum[i - 1] + 365 * traf0 * Math.pow(1 + rate / 100, i - 1) * 0.5;
    truckCum.push(trucks);
    esalCum.push(trucks * t2e / 0.95);
  }
  return { truckCum, esalCum };
}

// ─── Traffic charts ───────────────────────────────────────────────────────────
const trafficChartOptions = (yLabel: string) => ({
  responsive: true, maintainAspectRatio: false,
  interaction: { mode: 'index' as const, intersect: false },
  plugins: {
    legend: { display: false },
    tooltip: { bodyFont: { size: 12 }, titleFont: { size: 12 } },
  },
  scales: {
    x: { title: { display: true, text: 'Pavement age (years)', font: { size: 12 } }, grid: { color: 'rgba(0,0,0,0.05)' }, ticks: { maxRotation: 0, minRotation: 0, autoSkip: false, callback: (_: unknown, i: number, ticks: unknown[]) => { const v = i + 1; const last = ticks.length; const step = last <= 20 ? 5 : 10; return (v === 1 || v % step === 0 || i === last - 1) ? v : undefined; } } },
    y: { title: { display: true, text: yLabel, font: { size: 12 } }, min: 0, grid: { color: 'rgba(0,0,0,0.05)' } },
  },
});

const TrucksChart: React.FC<{ traf0: number; rate: number; iLife: number; iTrafficPattern: number }> = ({ traf0, rate, iLife, iTrafficPattern }) => {
  const { truckCum } = computeTrafficSeries(traf0, rate, iLife, iTrafficPattern);
  const labels = Array.from({ length: iLife }, (_, i) => i + 1);
  return (
    <div style={{ height: 280 }}>
      <Line data={{ labels, datasets: [{
        label: 'Cumulative heavy trucks (million)',
        data: truckCum.slice(1).map(v => v / 1e6),
        borderColor: PITT_BLUE, backgroundColor: `${PITT_BLUE}22`,
        borderWidth: 2, pointRadius: 3, fill: false, tension: 0,
      }]}} options={trafficChartOptions('Cumulative heavy trucks (million)')} />
    </div>
  );
};

const ESALsChart: React.FC<{ traf0: number; rate: number; iLife: number; iTrafficPattern: number }> = ({ traf0, rate, iLife, iTrafficPattern }) => {
  const { esalCum } = computeTrafficSeries(traf0, rate, iLife, iTrafficPattern);
  const labels = Array.from({ length: iLife }, (_, i) => i + 1);
  return (
    <div style={{ height: 280 }}>
      <Line data={{ labels, datasets: [{
        label: 'Cumulative ESALs (million)',
        data: esalCum.slice(1),
        borderColor: PITT_BLUE, backgroundColor: `${PITT_BLUE}22`,
        borderWidth: 2, pointRadius: 3, fill: false, tension: 0,
      }]}} options={trafficChartOptions('Cumulative ESALs (million)')} />
    </div>
  );
};


// ─── Instructions ─────────────────────────────────────────────────────────────
const Instructions: React.FC = () => {
  const [open, setOpen] = useState(false);
  return (
    <section className="bg-white rounded-2xl border border-gray-200 shadow-[0_2px_12px_rgba(0,53,148,0.06)] overflow-hidden">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50/60 transition-colors">
        <div className="flex items-center gap-3">
          <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16" className="text-[#003594] flex-shrink-0">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <span style={serifFont} className="font-semibold text-[#001f5b] text-lg">Instructions &amp; Parameter Definitions</span>
        </div>
        <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"
          className={`text-gray-400 transition-transform duration-200 flex-shrink-0 ${open ? 'rotate-180' : ''}`}>
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
      {open && (
        <div className="px-6 pb-6 border-t border-gray-100 pt-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { title: 'Traffic', items: [
                { term: 'Two-way AADTT', def: 'Annual average daily truck traffic, both directions combined.' },
                { term: 'Number of lanes', def: 'Total two-way lanes used to compute the design lane distribution factor.' },
                { term: 'Growth rate', def: 'Compound annual growth rate for truck traffic, %.' },
              ]},
              { title: 'Pavement structure', items: [
                { term: 'PCC Flexural Strength', def: 'Modulus of rupture at 28 days, psi.' },
                { term: 'COTE', def: 'Coefficient of thermal expansion of the PCC slab, ×10⁻⁶/°F.' },
                { term: 'Base type', def: 'Foundation layer beneath the PCC slab. Affects erosion and moisture parameters.' },
              ]},
              { title: 'Design targets', items: [
                { term: 'Cracking reliability', def: '% probability that actual cracking will not exceed the target.' },
                { term: 'Target % cracked slabs', def: 'Maximum allowable % cracked slabs at end of design life.' },
                { term: 'Target joint faulting', def: 'Maximum allowable mean joint faulting, in.' },
              ]},
            ].map(({ title, items }) => (
              <div key={title} className="bg-gray-50 rounded-xl p-4 space-y-2.5">
                <h3 style={serifFont} className="font-semibold text-[#001f5b] text-base mb-3">{title}</h3>
                {items.map(({ term, def }) => (
                  <div key={term}>
                    <span style={monoFont} className="text-xs font-medium text-[#003594]">{term}</span>
                    <p className="text-xs text-gray-500 leading-relaxed mt-0.5">{def}</p>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <p style={monoFont} className="text-xs text-[#003594] uppercase tracking-widest mb-1">Cite as</p>
              <p className="text-sm text-gray-600 leading-relaxed">
                Li, H., &amp; Khazanovich, L. (2021). PITTRIGID ME: Simplified Mechanistic-Empirical Design Tool
                for Pennsylvania Rigid Pavements. <em>J. Transp. Eng. Part B: Pavements</em>, 147(4), 04021052.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
            <a href="https://doi.org/10.1061/JPEODX.0000307" target="_blank" rel="noopener noreferrer"
              className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#003594] text-white text-sm font-semibold hover:bg-[#00276e] transition-colors whitespace-nowrap">
              Read Paper
              <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13">
                <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
              </svg>
            </a>
            <a href="https://pittrigid.azurewebsites.net/IRISE_PittRigid%20ME_Final%20Report_final(final%20version).pdf" target="_blank" rel="noopener noreferrer"
              className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-[#003594] text-[#003594] text-sm font-semibold hover:bg-[#003594] hover:text-white transition-colors whitespace-nowrap">
              Project Report
              <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13">
                <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z" clipRule="evenodd" />
              </svg>
            </a>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────
const PittRigidPage: React.FC = () => {
  const { status, results, run, clearResults } = usePittRigid();

  const [mode, setMode]               = useState<'design' | 'predict'>('design');
  const [climate, setClimate]         = useState(0);
  const [designLife, setDesignLife]   = useState(20);
  const [relCrack, setRelCrack]       = useState(90);
  const [relFault, setRelFault]       = useState(90);
  const [aadtt, setAadtt]             = useState(10000);
  const [nLanes, setNLanes]           = useState(0);
  const [growthRate, setGrowthRate]   = useState(3.0);
  const [traffPat, setTraffPat]       = useState(0);
  const [hPCC, setHPCC]               = useState(6.8);
  const [dowelIdx, setDowelIdx]       = useState(0);
  const [jointIdx, setJointIdx]       = useState(1);
  const [slabWidthIdx, setSlabWidthIdx] = useState(0);
  const [shoulderIdx, setShoulderIdx] = useState(0);
  const [coteIdx, setCoteIdx]         = useState(0);
  const [baseIdx, setBaseIdx]         = useState(0);
  const [mor, setMor]                 = useState(650);
  const [targetSlabs, setTargetSlabs] = useState(10.0);
  const [targetFault, setTargetFault] = useState(0.15);
  const [targetIri, setTargetIri]     = useState(DEFAULT_TARGET_IRI);
  const [preset, setPreset]           = useState<PresetKey>('national');
  const [coeffs, setCoeffs]           = useState<ModelCoeffs>({ ...PRESETS.national });

  const [showSettings, setShowSettings]     = useState(false);
  const [showClimateMap, setShowClimateMap] = useState(false);

  const handlePresetChange = (p: PresetKey) => {
    setPreset(p);
    if (p === 'custom') {
      // Seed custom with national so the user starts editing from a known baseline
      setCoeffs({ ...PRESETS.national });
    } else {
      setCoeffs({ ...PRESETS[p] });
    }
  };
  const setCoeff = (key: keyof typeof coeffs, val: number) => {
    setCoeffs(c => ({ ...c, [key]: val }));
    if (preset !== 'custom') setPreset('custom');
  };

  const laneFactor = LANE_FACTORS[nLanes] ?? 1.0;
  const traf0 = aadtt * laneFactor;

  const handleRun = useCallback(() => {
    const inp: PittRigidInputs = {
      iClimate: climate, iLife: designLife, traf0, rate: growthRate,
      iTrafficPattern: traffPat, hPCC, iDowel: dowelIdx,
      jpointspace: jointIdx, iwidth: slabWidthIdx, ishoulder: shoulderIdx,
      ialpPCC: coteIdx, ibaseType: baseIdx, pccMR28: mor,
      iRel: mode === 'design' ? 1 : 0,
      relCrack, relFault, targetSlabs, targetFault, ...coeffs,
    };
    run(inp);
  }, [climate, designLife, traf0, growthRate, traffPat, hPCC, dowelIdx,
      jointIdx, slabWidthIdx, shoulderIdx, coteIdx, baseIdx, mor,
      mode, relCrack, relFault, targetSlabs, targetFault, coeffs, run]);

  const handlePrint = useCallback(() => {
    if (!results) return;
    const { truckCum, esalCum } = computeTrafficSeries(traf0, growthRate, designLife, traffPat);
    const crack50  = results.crackAge[designLife - 1]?.toFixed(2)  ?? '—';
    const crackRel = results.crackAgeRel[designLife - 1]?.toFixed(2) ?? '—';
    const fault50  = results.fault[designLife - 1]?.toFixed(4)     ?? '—';
    const faultRel = results.faultRel[designLife - 1]?.toFixed(4)  ?? '—';
    const trucksM  = (truckCum[designLife] / 1e6).toFixed(2);
    const esalsM   = esalCum[designLife].toFixed(1);
    const printDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const climateLabel = CLIMATE_OPTIONS[climate] ?? '';

    // Build year-by-year table rows

    const inputs = [
      ['Climate',           climateLabel],
      ['Mode',              mode === 'design' ? 'Thickness Design' : 'Performance Prediction'],
      ['Design Life',       `${designLife} years`],
      ['Two-way AADTT',     `${aadtt.toLocaleString()}`],
      ['Growth Rate',       `${growthRate}%`],
      ['Traffic Pattern',   TRAFFIC_PATTERNS[traffPat]],
      ['Joint Spacing',     `${JOINT_SPACINGS[jointIdx]} ft`],
      ['Slab Width',        `${slabWidthIdx === 0 ? '12' : '15'} ft`],
      ['Shoulder Type',     SHOULDER_TYPES[shoulderIdx]],
      ['Base Type',         BASE_TYPES[baseIdx]],
      ['PCC Flexural Str.', `${mor} psi`],
      ['COTE',              `${COTE_VALUES[coteIdx]} ×10⁻⁶/°F`],
      ['Calibration',       PRESET_LABELS[preset]],
      ['Crack Reliability', `${relCrack}%`],
      ['Fault Reliability', `${relFault}%`],
      ...(mode === 'design' ? [
        ['Target cracked slabs', `${targetSlabs}%`],
        ['Target faulting',      `${targetFault} in`],
      ] : []),
    ];

    const results_tiles = [
      [mode === 'design' ? 'Required PCC thickness' : 'PCC thickness', `${results.hPCC.toFixed(2)} in`],
      [mode === 'design' ? 'Required dowel diameter' : 'Dowel diameter', results.dowelDiameter === 0 ? 'None' : `${results.dowelDiameter} in`],
      ['Cracking at 50%',          `${crack50} %`],
      [`Cracking at ${relCrack}%`, `${crackRel} %`],
      ['Faulting at 50%',          `${fault50} in`],
      [`Faulting at ${relFault}%`, `${faultRel} in`],
      ['Design lane trucks',       `${trucksM} M`],
      ['Design lane ESALs',        `${esalsM} M`],
    ];

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>PittRigid ME — Solution Report</title>
<link href="https://fonts.googleapis.com/css2?family=Crimson+Pro:ital,wght@0,300;0,400;0,600;1,400&family=DM+Mono:wght@400;500&family=Outfit:wght@400;500;600&display=swap" rel="stylesheet">
<script src="https://cdn.jsdelivr.net/npm/chart.js@4/dist/chart.umd.min.js"></script>
<style>
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Outfit', sans-serif; font-size: 10px; color: #111; background: white; }
  @page { size: letter portrait; margin: 14mm 16mm; }

  .header { border-bottom: 2px solid #111; padding-bottom: 7px; display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 10px; }
  .header h1 { font-family: 'Crimson Pro', serif; font-size: 24px; font-weight: 300; color: #111; line-height: 1; }
  .header .sub { font-size: 8.5px; color: #555; margin-top: 3px; }
  .header .meta { text-align: right; font-family: 'DM Mono', monospace; font-size: 8px; color: #555; line-height: 1.7; }

  .section-title { font-family: 'Crimson Pro', serif; font-size: 12px; font-weight: 600; color: #001f5b; border-bottom: 1px solid #bbb; padding-bottom: 2px; margin-bottom: 7px; margin-top: 12px; }

  .inputs { display: grid; grid-template-columns: repeat(3, 1fr); gap: 2px 20px; background: #f5f5f5; border-radius: 4px; padding: 8px 0; }
  .input-row { display: flex; gap: 5px; line-height: 1.75; font-size: 9.5px; }
  .input-label { color: #444; white-space: nowrap; }
  .input-value { font-family: 'DM Mono', monospace; color: #111; font-weight: 500; }

  .tiles { display: grid; grid-template-columns: repeat(4, 1fr); gap: 5px; margin-bottom: 2px; }
  .tile { border: 1px solid #e8e8e8; border-radius: 4px; padding: 6px 8px; }
  .tile-label { font-size: 7.5px; text-transform: uppercase; letter-spacing: 0.07em; color: #555; margin-bottom: 2px; }
  .tile-value { font-family: 'Crimson Pro', serif; font-size: 18px; font-weight: 600; color: #111; line-height: 1; }

  .charts-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
  .chart-box { border: 1px solid #e8e8e8; border-radius: 4px; padding: 8px; }
  .chart-title { font-family: 'Crimson Pro', serif; font-size: 10.5px; font-weight: 600; color: #001f5b; text-align: center; margin-bottom: 4px; }
  canvas { display: block; }

  .footer { margin-top: 10px; padding-top: 5px; border-top: 1px solid #bbb; display: flex; justify-content: space-between; font-size: 7px; color: #666; }
</style>
</head>
<body>

<div class="header">
  <div>
    <h1>PittRigid ME</h1>
    <p class="sub">Mechanistic-Empirical Design for Pennsylvania Rigid Pavements</p>
  </div>
  <div class="meta">
    <div>${mode === 'design' ? 'Thickness Design' : 'Performance Prediction'}</div>
    <div>${printDate}</div>
  </div>
</div>

<div class="section-title">Design Inputs</div>
<div class="inputs">
  ${inputs.map(([l, v]) => `<div class="input-row"><span class="input-label">${l}:</span><span class="input-value">${v}</span></div>`).join('')}
</div>

<div class="section-title">Results Summary</div>
<div class="tiles">
  ${results_tiles.map(([l, v]) => `<div class="tile"><div class="tile-label">${l}</div><div class="tile-value">${v}</div></div>`).join('')}
</div>

<div class="section-title">Performance Charts</div>
<div class="charts-grid">
  <div class="chart-box"><div class="chart-title">Transverse Cracking</div><canvas id="c1" height="140"></canvas></div>
  <div class="chart-box"><div class="chart-title">Joint Faulting</div><canvas id="c2" height="140"></canvas></div>
  <div class="chart-box"><div class="chart-title">Cumulative Heavy Trucks</div><canvas id="c3" height="140"></canvas></div>
  <div class="chart-box"><div class="chart-title">Cumulative ESALs</div><canvas id="c4" height="140"></canvas></div>
</div>

<div class="footer">
  <span>Li, H. &amp; Khazanovich, L. (2021). PITTRIGID ME. J. Transp. Eng. Part B: Pavements, 147(4), 04021052.</span>
  <span>Generated by PittRigid ME &middot; University of Pittsburgh</span>
</div>

<script>
const BLUE = '#003594', DARK = '#444', RED = '#222';
const years = ${JSON.stringify(Array.from({ length: designLife }, (_, i) => i + 1))};
const crackAge    = ${JSON.stringify(results.crackAge.map(v => +v.toFixed(4)))};
const crackAgeRel = ${JSON.stringify(results.crackAgeRel.map(v => +v.toFixed(4)))};
const fault       = ${JSON.stringify(results.fault.map(v => +v.toFixed(6)))};
const faultRel    = ${JSON.stringify(results.faultRel.map(v => +v.toFixed(6)))};
const trucksData  = ${JSON.stringify(truckCum.slice(1).map(v => +(v/1e6).toFixed(3)))};
const esalsData   = ${JSON.stringify(esalCum.slice(1).map(v => +v.toFixed(3)))};
const targetSlabs = ${targetSlabs};
const targetFaultV= ${targetFault};
const relCrackV   = ${relCrack};
const relFaultV   = ${relFault};
const crackMax    = Math.ceil(Math.max(targetSlabs, ...crackAgeRel) * 1.2);

const opts = (yLabel, yMax, showLegend = true) => ({
  responsive: false, animation: false,
  plugins: { legend: { display: showLegend, position: 'top', labels: { font: { size: 7 }, boxWidth: 6, boxHeight: 6, padding: 5, color: '#111', usePointStyle: true, pointStyleWidth: 6 } } },
  scales: {
    x: { title: { display: true, text: 'Pavement age (years)', font: { size: 8 }, color: '#333' }, ticks: { font: { size: 7.5 }, color: '#333', maxRotation: 0, minRotation: 0, autoSkip: false, callback: function(_, i, ticks) { var v = i + 1; var last = ticks.length; var step = last <= 20 ? 5 : 10; return (v === 1 || v % step === 0 || i === last - 1) ? v : undefined; } }, grid: { color: '#eee' } },
    y: { title: { display: true, text: yLabel, font: { size: 8 }, color: '#333' }, ticks: { font: { size: 7.5 }, color: '#333' }, grid: { color: '#eee' }, min: 0, ...(yMax !== undefined ? { max: yMax } : {}) }
  }
});

window.onload = () => {
  new Chart(document.getElementById('c1'), { type: 'line', data: { labels: years, datasets: [
    { label: '50% reliability', data: crackAge, borderColor: BLUE, borderWidth: 1.5, pointStyle: 'circle', pointRadius: 1.5, pointBackgroundColor: BLUE, fill: false, tension: 0.3 },
    { label: relCrackV+'% reliability', data: crackAgeRel, borderColor: DARK, borderWidth: 1.5, borderDash: [4,3], pointStyle: 'triangle', pointRadius: 2, pointBackgroundColor: DARK, fill: false, tension: 0.3 },
    { label: 'Target', data: years.map(() => targetSlabs), borderColor: RED, borderWidth: 1, borderDash: [6,4], pointStyle: 'dash', pointRadius: 0, fill: false },
  ]}, options: opts('Cracked slabs (%)', crackMax) });

  new Chart(document.getElementById('c2'), { type: 'line', data: { labels: years, datasets: [
    { label: '50% reliability', data: fault, borderColor: BLUE, borderWidth: 1.5, pointStyle: 'circle', pointRadius: 1.5, pointBackgroundColor: BLUE, fill: false, tension: 0.3 },
    { label: relFaultV+'% reliability', data: faultRel, borderColor: DARK, borderWidth: 1.5, borderDash: [4,3], pointStyle: 'triangle', pointRadius: 2, pointBackgroundColor: DARK, fill: false, tension: 0.3 },
    { label: 'Target', data: years.map(() => targetFaultV), borderColor: RED, borderWidth: 1, borderDash: [6,4], pointStyle: 'dash', pointRadius: 0, fill: false },
  ]}, options: opts('Joint faulting (in)') });

  new Chart(document.getElementById('c3'), { type: 'line', data: { labels: years, datasets: [
    { label: 'Trucks (M)', data: trucksData, borderColor: BLUE, borderWidth: 1.5, pointStyle: 'circle', pointRadius: 1.5, pointBackgroundColor: BLUE, fill: false, tension: 0 },
  ]}, options: opts('Cumulative trucks (M)', undefined, false) });

  new Chart(document.getElementById('c4'), { type: 'line', data: { labels: years, datasets: [
    { label: 'ESALs (M)', data: esalsData, borderColor: BLUE, borderWidth: 1.5, pointStyle: 'circle', pointRadius: 1.5, pointBackgroundColor: BLUE, fill: false, tension: 0 },
  ]}, options: opts('Cumulative ESALs (M)', undefined, false) });

  setTimeout(() => window.print(), 900);
};
</script>
</body>
</html>`;

    const w = window.open('', '_blank', 'width=900,height=700');
    if (w) {
      w.document.write(html);
      w.document.close();
    }
  }, [results, traf0, growthRate, designLife, traffPat, climate, mode,
      aadtt, jointIdx, slabWidthIdx, shoulderIdx, baseIdx,
      mor, coteIdx, preset, relCrack, relFault, targetSlabs, targetFault]);

  const isRunning = status.kind === 'running';
  const isLoading = status.kind === 'loading' || status.kind === 'idle';
  const canRun    = !isRunning && !isLoading;

  // IRI development series (built from the completed run). Null until a run finishes.
  const iriSeries: IriSeries | null = results
    ? computeIriSeries({ iClimate: climate, jpointspace: jointIdx, pccMR28: mor }, results)
    : null;

  // Cumulative design-lane traffic at end of design life (for the traffic card).
  const trafficTotals = results
    ? (() => {
        const { truckCum, esalCum } = computeTrafficSeries(traf0, growthRate, designLife, traffPat);
        return {
          trucks: truckCum[designLife].toLocaleString('en-US', { maximumFractionDigits: 0 }),
          esals:  (esalCum[designLife] * 1e6).toLocaleString('en-US', { maximumFractionDigits: 0 }),
        };
      })()
    : null;

  const statusText = status.kind === 'error'   ? status.message
    : status.kind === 'running' ? 'Running…'
    : status.kind === 'done'    ? 'Done'
    : status.kind === 'ready'   ? 'Ready'
    : status.kind === 'loading' ? 'Loading databases…'
    : 'Initializing…';

  const statusCls = status.kind === 'error'   ? 'text-red-400'
    : status.kind === 'running' ? 'text-blue-300 animate-pulse'
    : status.kind === 'done'    ? 'text-green-400'
    : status.kind === 'ready'   ? 'text-green-400'
    : 'text-yellow-400';

  const statusDot = status.kind === 'error'   ? 'bg-red-400'
    : status.kind === 'running' ? 'bg-blue-400 animate-pulse'
    : status.kind === 'done'    ? 'bg-green-400'
    : status.kind === 'ready'   ? 'bg-green-400'
    : 'bg-yellow-400 animate-pulse';

  return (
    <div id="pittrigid-app" className="min-h-screen bg-[#f0f2f5]" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Crimson+Pro:ital,wght@0,400;0,600;1,400&family=DM+Mono:wght@400;500&family=Outfit:wght@400;500;600&display=swap');

        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }

      `}</style>
      <Navbar />

      {/* Header */}
      <div className="bg-[#001f5b] border-b-2 border-[#FFB81C]">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-end justify-between">
          <div>
            <p style={monoFont} className="text-[#FFB81C] text-xs tracking-[0.18em] uppercase mb-1.5 opacity-80">
              Rigid Pavements · Design
            </p>
            <h1 style={serifFont} className="text-4xl font-light text-white leading-none">PittRigid ME</h1>
            <p className="text-white/50 text-sm mt-1.5">Mechanistic-empirical design for Pennsylvania rigid pavements</p>
          </div>
          <div className="flex items-center gap-2 pb-1">
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${statusDot}`} />
            <span style={monoFont} className={`text-sm ${statusCls}`}>{statusText}</span>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-5">

        <Instructions />

        {/* ── Main form card ──────────────────────────────────────────────── */}
        <section className="bg-white rounded-2xl border border-gray-200 shadow-[0_2px_12px_rgba(0,53,148,0.07)]">
          <div className="px-6 py-5 space-y-6">

            {/* SECTION: Analysis Options — 4-col grid: Mode | Climate (×2) | Design Life */}
            <div>
              <SectionHead>Analysis Options</SectionHead>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Field label="Mode">
                  <select value={mode} onChange={e => { setMode(e.target.value as 'design' | 'predict'); clearResults(); }} className={selectCls}>
                    <option value="design">Design</option>
                    <option value="predict">Performance Prediction</option>
                  </select>
                </Field>
                <div className="lg:col-span-2">
                  <Field label="Climate Region">
                    <div className="flex gap-2">
                      <select value={climate} onChange={e => { setClimate(+e.target.value); clearResults(); }} className={selectCls}>
                        {CLIMATE_OPTIONS.map((o, i) => <option key={i} value={i}>{o}</option>)}
                      </select>
                      <button onClick={() => setShowClimateMap(true)} title="View climate map"
                        className="flex-shrink-0 flex items-center gap-1.5 px-3 rounded border border-gray-300 text-gray-500 hover:text-[#003594] hover:border-[#003594] transition-colors bg-white text-sm">
                        <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
                          <path fillRule="evenodd" d="M12 1.586l-4 4V17h8V5.586l-4-4zM4 5a1 1 0 011-1h1a1 1 0 010 2H5a1 1 0 01-1-1zm11 0a1 1 0 00-1-1h-1a1 1 0 000 2h1a1 1 0 001-1zM3 9a1 1 0 000 2h14a1 1 0 100-2H3zm0 4a1 1 0 000 2h14a1 1 0 100-2H3z" clipRule="evenodd"/>
                        </svg>
                        Map
                      </button>
                    </div>
                  </Field>
                </div>
                <Field label="Design Life, years">
                  <NumInput value={designLife} onChange={v => { setDesignLife(Math.round(v)); clearResults(); }} min="1" max="60" />
                </Field>
              </div>
            </div>

            {/* SECTION: Traffic — 4-col grid: AADTT | Lanes | Growth Rate | Pattern */}
            <div>
              <SectionHead>Traffic</SectionHead>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Field label="Two-way AADTT in Year 1">
                  <NumInput value={aadtt} onChange={v => { setAadtt(v); clearResults(); }} min="1" />
                </Field>
                <Field label="Number of Lanes (two way)">
                  <select value={nLanes} onChange={e => { setNLanes(+e.target.value); clearResults(); }} className={selectCls}>
                    {LANES_OPTIONS.map((n, i) => <option key={i} value={i}>{n}</option>)}
                  </select>
                </Field>
                <Field label="Compound Growth Rate, %">
                  <NumInput value={growthRate} onChange={v => { setGrowthRate(v); clearResults(); }} step="0.1" min="0" />
                </Field>
                <Field label="Traffic Pattern">
                  <select value={traffPat} onChange={e => { setTraffPat(+e.target.value); clearResults(); }} className={selectCls}>
                    {TRAFFIC_PATTERNS.map((o, i) => <option key={i} value={i}>{o}</option>)}
                  </select>
                </Field>
              </div>
            </div>

            {/* SECTION: Pavement Structure
                Design mode  — 6 fields, rows of 4: [Spacing | Slab | Shoulder | Base] [MR | COTE | — | —]
                Predict mode — 8 fields, rows of 4: [Thick | Dowel | Spacing | Slab] [Shoulder | Base | MR | COTE] */}
            <div>
              <SectionHead>Pavement Structure</SectionHead>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {mode === 'predict' && (
                  <Field label="PCC Thickness, in">
                    <NumInput value={hPCC} onChange={v => { setHPCC(v); clearResults(); }} step="0.1" min="6" max="14" />
                  </Field>
                )}
                {mode === 'predict' && (
                  <Field label="Dowel Diameter">
                    <select value={dowelIdx} onChange={e => { setDowelIdx(+e.target.value); clearResults(); }} className={selectCls}>
                      {DOWEL_DIAMETERS.map((d, i) => <option key={i} value={i}>{d === 0 ? 'None (undoweled)' : `${d} in`}</option>)}
                    </select>
                  </Field>
                )}
                <Field label="Joint Spacing, ft">
                  <select value={jointIdx} onChange={e => { setJointIdx(+e.target.value); clearResults(); }} className={selectCls}>
                    {JOINT_SPACINGS.map((s, i) => <option key={i} value={i}>{s} ft</option>)}
                  </select>
                </Field>
                <Field label="Slab Width">
                  <select value={slabWidthIdx} onChange={e => { setSlabWidthIdx(+e.target.value); clearResults(); }} className={selectCls}>
                    {SLAB_WIDTHS.map((s, i) => <option key={i} value={i}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Shoulder Type">
                  <select value={shoulderIdx} onChange={e => { setShoulderIdx(+e.target.value); clearResults(); }} className={selectCls}>
                    {SHOULDER_TYPES.map((s, i) => <option key={i} value={i}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Base Type">
                  <select value={baseIdx} onChange={e => { setBaseIdx(+e.target.value); clearResults(); }} className={selectCls}>
                    {BASE_TYPES.map((s, i) => <option key={i} value={i}>{s}</option>)}
                  </select>
                </Field>
                <Field label="PCC Flexural Strength, psi">
                  <NumInput value={mor} onChange={v => { setMor(v); clearResults(); }} step="1" min="400" max="1400" />
                </Field>
                <Field label={<>COTE, 10<sup>-6</sup> 1/°F</>}>
                  <select value={coteIdx} onChange={e => { setCoteIdx(+e.target.value); clearResults(); }} className={selectCls}>
                    {COTE_VALUES.map((c, i) => <option key={i} value={i}>{c}</option>)}
                  </select>
                </Field>
              </div>
            </div>

            <hr className="border-gray-100" />

            {/* SECTION: Calibration row + Show Settings toggle */}
            <div>
              {/* 4-col grid: Preset (×2) | Show Settings btn | [empty] */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                <div className="lg:col-span-2">
                  <Field label="Calibration Parameter Set">
                    <select value={preset} onChange={e => handlePresetChange(e.target.value as PresetKey)} className={selectCls}>
                      {(Object.keys(PRESET_LABELS) as PresetKey[]).map(k => (
                        <option key={k} value={k}>{PRESET_LABELS[k]}</option>
                      ))}
                    </select>
                  </Field>
                </div>
                <div className="flex items-end">
                  <button onClick={() => setShowSettings(s => !s)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded border border-gray-300 text-sm font-medium text-gray-600 bg-white hover:bg-gray-50 hover:border-[#003594] hover:text-[#003594] transition-colors">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="15" height="15">
                      <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                    </svg>
                    {showSettings ? 'Hide Settings' : 'Show Settings'}
                  </button>
                </div>
              </div>

              {/* Settings panel */}
              {showSettings && (
                <div className="mt-5 pt-5 border-t border-gray-100 space-y-6">

                  {/* Reliability + Design Targets — 4-col grid */}
                  <div>
                    <SectionHead>Reliability &amp; Design Targets</SectionHead>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <Field label="Cracking Reliability, %">
                        <NumInput value={relCrack} onChange={v => { setRelCrack(Math.round(v)); clearResults(); }} min="50" max="99" />
                      </Field>
                      <Field label="Faulting Reliability, %">
                        <NumInput value={relFault} onChange={v => { setRelFault(Math.round(v)); clearResults(); }} min="50" max="99" />
                      </Field>
                      {mode === 'design' && (
                        <Field label="Target Cracked Slabs, %">
                          <NumInput value={targetSlabs} onChange={v => { setTargetSlabs(v); clearResults(); }} step="1" min="0" max="100" />
                        </Field>
                      )}
                      {mode === 'design' && (
                        <Field label="Target Joint Faulting, in">
                          <NumInput value={targetFault} onChange={v => { setTargetFault(v); clearResults(); }} step="0.01" min="0" />
                        </Field>
                      )}
                      <Field label="Design Limit IRI, in/mi">
                        <NumInput value={targetIri} onChange={v => { setTargetIri(v); clearResults(); }} step="1" min="0" />
                      </Field>
                    </div>
                  </div>

                  {/* Calibration coefficients — 2-col split */}
                  <div>
                    <SectionHead>Calibration Coefficients</SectionHead>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <p style={monoFont} className="text-xs uppercase tracking-widest text-gray-500 mb-3">Cracking model</p>
                        <div className="space-y-2">
                          <CoeffRow label="C1" value={coeffs.crackC1d} onChange={v => setCoeff('crackC1d', v)} readOnly={preset !== 'custom'} />
                          <CoeffRow label="C2" value={coeffs.crackC2d} onChange={v => setCoeff('crackC2d', v)} readOnly={preset !== 'custom'} />
                        </div>
                      </div>
                      <div>
                        <p style={monoFont} className="text-xs uppercase tracking-widest text-gray-500 mb-3">Faulting model</p>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-2" style={{ gridAutoFlow: 'column', gridTemplateRows: 'repeat(4, auto)' }}>
                          <CoeffRow label="C1" value={coeffs.c1Fault} onChange={v => setCoeff('c1Fault', v)} readOnly={preset !== 'custom'} />
                          <CoeffRow label="C2" value={coeffs.c2Fault} onChange={v => setCoeff('c2Fault', v)} readOnly={preset !== 'custom'} />
                          <CoeffRow label="C3" value={coeffs.c3Fault} onChange={v => setCoeff('c3Fault', v)} readOnly={preset !== 'custom'} />
                          <CoeffRow label="C4" value={coeffs.c4Fault} onChange={v => setCoeff('c4Fault', v)} readOnly={preset !== 'custom'} />
                          <CoeffRow label="C5" value={coeffs.c5Fault} onChange={v => setCoeff('c5Fault', v)} readOnly={preset !== 'custom'} />
                          <CoeffRow label="C6" value={coeffs.c6Fault} onChange={v => setCoeff('c6Fault', v)} readOnly={preset !== 'custom'} />
                          <CoeffRow label="C7" value={coeffs.c7Fault} onChange={v => setCoeff('c7Fault', v)} readOnly={preset !== 'custom'} />
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              )}
            </div>

          </div>{/* end px-6 py-5 */}

          {/* Run bar */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-2xl flex items-center gap-3">
            <button onClick={handleRun} disabled={!canRun}
              className="px-8 py-2.5 rounded-lg bg-[#003594] text-white font-semibold text-sm hover:bg-[#00276e] disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-[0_2px_10px_rgba(0,53,148,0.22)]">
              {isRunning ? 'Running…' : 'Run Analysis'}
            </button>
            {status.kind === 'error' && (
              <span className="text-sm text-red-500 truncate">{status.message}</span>
            )}
          </div>

        </section>

        {/* ── Results ──────────────────────────────────────────────────────── */}
        {results && (
          <div className="space-y-5 animate-[fadeIn_0.25s_ease-out]">
            <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }`}</style>
            <section className="bg-white rounded-2xl border border-gray-200 shadow-[0_2px_12px_rgba(0,53,148,0.07)] p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 style={serifFont} className="text-2xl font-semibold text-[#001f5b]">Results</h2>
                <span style={monoFont} className="text-xs text-gray-400">
                  {mode === 'design' ? 'Design mode' : 'Performance prediction'}
                </span>
              </div>
              {(() => {
                const crack50num  = results.crackAge[designLife - 1]    ?? 0;
                const crackRelNum = results.crackAgeRel[designLife - 1] ?? 0;
                const fault50num  = results.fault[designLife - 1]       ?? 0;
                const faultRelNum = results.faultRel[designLife - 1]    ?? 0;
                const lastIri  = iriSeries?.years[iriSeries.years.length - 1];
                const iri50num = lastIri?.iri50 ?? 0;
                const iriRelNum = lastIri?.iriRel ?? 0;

                const rows = [
                  { name: 'Transverse cracking', unit: '%',     v50: crack50num.toFixed(2), vRel: crackRelNum.toFixed(2), limit: `${targetSlabs}`, pass: crackRelNum <= targetSlabs },
                  { name: 'Joint faulting',      unit: 'in',    v50: fault50num.toFixed(3), vRel: faultRelNum.toFixed(3), limit: `${targetFault}`, pass: faultRelNum <= targetFault },
                  { name: 'IRI',                 unit: 'in/mi', v50: iri50num.toFixed(1),   vRel: iriRelNum.toFixed(1),   limit: `${targetIri}`,   pass: iriRelNum <= targetIri },
                ];

                return (
                  <div className="mb-8 space-y-7">
                    {/* Design outcome — the answer */}
                    <div className="rounded-2xl bg-[#001f5b] overflow-hidden shadow-[0_4px_20px_rgba(0,31,91,0.18)]">
                      <div className="h-1 bg-[#FFB81C]" />
                      <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 sm:divide-x sm:divide-white/15">
                        <div className="py-2 sm:pr-8">
                          <p style={monoFont} className="text-[0.65rem] uppercase tracking-[0.15em] text-[#FFB81C] mb-1.5">
                            {mode === 'design' ? 'Required slab thickness' : 'PCC slab thickness'}
                          </p>
                          <p style={serifFont} className="text-4xl font-semibold text-white leading-none">
                            {results.hPCC.toFixed(2)}<span className="text-xl font-normal text-white/55 ml-1.5">in</span>
                          </p>
                        </div>
                        <div className="py-2 sm:pl-8 mt-4 sm:mt-0">
                          <p style={monoFont} className="text-[0.65rem] uppercase tracking-[0.15em] text-[#FFB81C] mb-1.5">
                            {mode === 'design' ? 'Required dowel diameter' : 'Dowel diameter'}
                          </p>
                          <p style={serifFont} className="text-4xl font-semibold text-white leading-none">
                            {results.dowelDiameter === 0
                              ? <span className="text-3xl">None</span>
                              : <>{results.dowelDiameter}<span className="text-xl font-normal text-white/55 ml-1.5">in</span></>}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* End-of-life performance table */}
                    <div>
                      <p style={monoFont} className="text-[0.65rem] uppercase tracking-[0.15em] text-gray-500 mb-2">
                        Performance at year {designLife}
                      </p>
                      <div className="rounded-xl border border-gray-200 overflow-hidden">
                        <div className="grid grid-cols-[1.7fr_1fr_1fr_1fr] bg-gray-50 border-b border-gray-200">
                          <span style={monoFont} className="px-4 py-2.5 text-[0.7rem] uppercase tracking-wider text-gray-500">Distress</span>
                          <span style={monoFont} className="px-4 py-2.5 text-[0.7rem] uppercase tracking-wider text-gray-500 text-right">50%</span>
                          <span style={monoFont} className="px-4 py-2.5 text-[0.7rem] uppercase tracking-wider text-gray-500 text-right">{relCrack}%</span>
                          <span style={monoFont} className="px-4 py-2.5 text-[0.7rem] uppercase tracking-wider text-gray-500 text-right">Limit</span>
                        </div>
                        {rows.map((r, i) => (
                          <div key={r.name} className={`grid grid-cols-[1.7fr_1fr_1fr_1fr] items-center ${i > 0 ? 'border-t border-gray-100' : ''}`}>
                            <span className="px-4 py-3 text-sm text-gray-700">{r.name} <span className="text-gray-400 text-xs">({r.unit})</span></span>
                            <span style={monoFont} className="px-4 py-3 text-sm text-gray-900 text-right tabular-nums">{r.v50}</span>
                            <span style={monoFont} className={`px-4 py-3 text-sm text-right tabular-nums font-medium ${r.pass ? 'text-emerald-700' : 'text-red-600'}`}>{r.vRel}</span>
                            <span style={monoFont} className="px-4 py-3 text-sm text-gray-400 text-right tabular-nums">{r.limit}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* ── Predicted performance charts ─────────────────────────────── */}
              <SectionHead>Predicted Performance</SectionHead>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <p style={serifFont} className="text-base font-semibold text-[#001f5b] mb-3 text-center">Transverse Cracking</p>
                  <div className="mx-auto" style={{ maxWidth: '78%' }}>
                    <CrackingChart results={results} relCrack={relCrack} targetSlabs={targetSlabs} />
                  </div>
                </div>
                <div>
                  <p style={serifFont} className="text-base font-semibold text-[#001f5b] mb-3 text-center">Joint Faulting</p>
                  <div className="mx-auto" style={{ maxWidth: '78%' }}>
                    <FaultingChart results={results} relFault={relFault} targetFault={targetFault} />
                  </div>
                </div>
              </div>

              {iriSeries && (
                <div className="mt-8">
                  <p style={serifFont} className="text-base font-semibold text-[#001f5b] mb-3 text-center">IRI Development</p>
                  <div className="mx-auto" style={{ maxWidth: '40%' }}>
                    <IRIChart series={iriSeries} relIri={relCrack} targetIri={targetIri} />
                  </div>
                </div>
              )}

              {/* ── Traffic loading ──────────────────────────────────────────── */}
              <div className="mt-10">
                <SectionHead>Traffic Loading</SectionHead>
              </div>
              {trafficTotals && (
                <div className="rounded-xl border border-gray-200 bg-[#f7f6f2] px-6 py-4 mb-7 grid grid-cols-1 sm:grid-cols-2 sm:divide-x sm:divide-gray-200">
                  <div className="py-1 sm:pr-8">
                    <p style={monoFont} className="text-[0.65rem] uppercase tracking-[0.15em] text-gray-500 mb-1">Cumulative heavy trucks</p>
                    <p style={serifFont} className="text-2xl font-semibold text-[#001f5b] leading-none tabular-nums">{trafficTotals.trucks}</p>
                  </div>
                  <div className="py-1 sm:pl-8 mt-3 sm:mt-0">
                    <p style={monoFont} className="text-[0.65rem] uppercase tracking-[0.15em] text-gray-500 mb-1">Cumulative ESALs</p>
                    <p style={serifFont} className="text-2xl font-semibold text-[#001f5b] leading-none tabular-nums">{trafficTotals.esals}</p>
                  </div>
                  <p className="text-[0.7rem] text-gray-400 mt-3 sm:col-span-2">Design lane, over the {designLife}-year design life.</p>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <p style={serifFont} className="text-base font-semibold text-[#001f5b] mb-3 text-center">Cumulative Heavy Trucks</p>
                  <div className="mx-auto" style={{ maxWidth: '78%' }}>
                    <TrucksChart traf0={traf0} rate={growthRate} iLife={designLife} iTrafficPattern={traffPat} />
                  </div>
                </div>
                <div>
                  <p style={serifFont} className="text-base font-semibold text-[#001f5b] mb-3 text-center">Cumulative ESALs</p>
                  <div className="mx-auto" style={{ maxWidth: '78%' }}>
                    <ESALsChart traf0={traf0} rate={growthRate} iLife={designLife} iTrafficPattern={traffPat} />
                  </div>
                </div>
              </div>

              {/* ── Print Solution button ──────────────────────────────────── */}
              <div className="mt-8 pt-6 border-t border-gray-100 flex justify-center">
                <button
                  onClick={() => handlePrint()}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[#001f5b] text-white text-sm font-semibold hover:bg-[#003594] transition-colors shadow-[0_2px_10px_rgba(0,31,91,0.2)]"
                >
                  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                    <path fillRule="evenodd" d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a1 1 0 001 1h8a1 1 0 001-1v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a1 1 0 00-1-1H6a1 1 0 00-1 1zm2 0h6v3H7V4zm-1 9h8v3H6v-3zm8-4a1 1 0 110 2 1 1 0 010-2z" clipRule="evenodd" />
                  </svg>
                  Print Solution
                </button>
              </div>
            </section>

          </div>
        )}

      </main>

      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <p style={monoFont} className="text-[0.65rem] text-[#003594] uppercase tracking-widest mb-1">Developed by</p>
            <p className="text-xs text-gray-600 leading-relaxed">
              <a href="https://www.linkedin.com/in/jorgeolava/en" target="_blank" rel="noopener noreferrer"
                className="font-medium text-[#003594] hover:underline">Jorge Olavarria</a>
              {' & '}
              <a href="https://www.linkedin.com/in/lev-khazanovich-44133b5" target="_blank" rel="noopener noreferrer"
                className="font-medium text-[#003594] hover:underline">Lev Khazanovich</a>
              {' · University of Pittsburgh, Department of Civil & Environmental Engineering · 2026'}
            </p>
          </div>
        </div>
      </div>

      <Footer />
      {showClimateMap && <ClimateMapModal onClose={() => setShowClimateMap(false)} />}

    </div>
  );
};

export default PittRigidPage;